import { useEffect, useState } from "react";
import AutocompleteSearchBar from "./AutocompleteSearchBar";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  MapContainer,
  useMap,
  useMapEvents,
  AttributionControl,
  Polyline,
  Polygon,
  LayerGroup,
} from "react-leaflet";
import { latLng, latLngBounds, GridLayer } from "leaflet";
import classes from "./RKMap.module.scss";
import auth from "../util/auth";
import { nodes } from "../data/nodes";
import Control from "react-leaflet-custom-control";
import NodeBox from "./boxes/NodeBox";
import GridCellBox from "./boxes/GridCellBox";
import ArmiesBox from "./boxes/ArmiesBox";
import HistoryBox from "./boxes/HistoryBox";
import axios from "axios";
import NodeMarker from "./markers/NodeMarker";
import ArmyMarker from "./markers/ArmyMarker";
import { icons } from "../assets/icons/Icons";
import ControlButton from "./buttons/ControlButton";
import CachedTileLayer from "./tiles/CachedTileLayer";
import { findShortestPathBFS } from "../util/pathfinding";

const GRID_IMAGE_WIDTH = 19200;
const GRID_IMAGE_HEIGHT = 21200;
const GRID_CELL_SIZE = 80;
const GRID_COLS = 240;
const GRID_ROWS = 265;
const GRID_X_OFFSET = 60;
const GRID_Y_OFFSET = 25;
const GRID_ZOOM = 7;

const GridOverlay = ({ selectedGridCell }) => {
  const map = useMap();

  const unproject = (x, y) => {
    const point = map.unproject([x, y], GRID_ZOOM);
    return [point.lat, point.lng];
  };

  const lines = [];

  for (let i = 0; i <= GRID_COLS; i++) {
    lines.push({
      key: `grid-v-${i}`,
      positions: [
        unproject(i * GRID_CELL_SIZE, 0),
        unproject(i * GRID_CELL_SIZE, GRID_IMAGE_HEIGHT),
      ],
      color: "yellow",
      weight: i % 20 === 0 ? 2 : 1,
    });
  }

  for (let i = 0; i <= GRID_ROWS; i++) {
    lines.push({
      key: `grid-h-${i}`,
      positions: [
        unproject(0, i * GRID_CELL_SIZE),
        unproject(GRID_IMAGE_WIDTH, i * GRID_CELL_SIZE),
      ],
      color: "orange",
      weight: i % 25 === 0 ? 2 : 1,
    });
  }

  const highlight = selectedGridCell
    ? (() => {
      const cellX = selectedGridCell.x - GRID_X_OFFSET;
      const cellY = selectedGridCell.y - GRID_Y_OFFSET;
      if (cellX < 0 || cellX >= GRID_COLS || cellY < 0 || cellY >= GRID_ROWS) {
        return null;
      }
      const topLeft = unproject(cellX * GRID_CELL_SIZE, cellY * GRID_CELL_SIZE);
      const topRight = unproject((cellX + 1) * GRID_CELL_SIZE, cellY * GRID_CELL_SIZE);
      const bottomRight = unproject((cellX + 1) * GRID_CELL_SIZE, (cellY + 1) * GRID_CELL_SIZE);
      const bottomLeft = unproject(cellX * GRID_CELL_SIZE, (cellY + 1) * GRID_CELL_SIZE);
      return [topLeft, topRight, bottomRight, bottomLeft];
    })()
    : null;

  return (
    <LayerGroup>
      {lines.map((line) => (
        <Polyline
          key={line.key}
          positions={line.positions}
          color={line.color}
          weight={line.weight}
          opacity={0}
          interactive={false}
        />
      ))}
      {highlight && (
        <Polygon
          positions={highlight}
          pathOptions={{ fillColor: "lightgreen", fillOpacity: 0.32, color: "green", weight: 1 }}
          interactive={false}
        />
      )}
    </LayerGroup>
  );
};

const MapClickLogger = ({ showGrid, onGridCellClick }) => {
  const map = useMapEvents({
    click: (e) => {
      if (showGrid) {
        const point = map.project(e.latlng, GRID_ZOOM);
        const click_x = Math.floor(point.x / GRID_CELL_SIZE);
        const click_y = Math.floor(point.y / GRID_CELL_SIZE);
        const inside = click_x >= 0 && click_x < GRID_COLS && click_y >= 0 && click_y < GRID_ROWS;
        if (inside) {
          if (typeof onGridCellClick === "function") {
            onGridCellClick({ x: click_x + GRID_X_OFFSET, y: click_y + GRID_Y_OFFSET });
          }
        }
      } else {
        console.log(`Clicked at: ${e.latlng.lat.toFixed(2)}, ${e.latlng.lng.toFixed(2)}`);
      }
    },
  });

  return null;
};

const ZoomListener = ({ setZoomLevel }) => {
  const mapEvents = useMapEvents({
    zoomend: () => {
      setZoomLevel(mapEvents.getZoom());
    },
  });
  return null;
};

const MoveListener = ({ setCenter }) => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      setCenter([center.lat, center.lng]);
    },
  });
  return null;
};

const RKMap = () => {
  const navigate = useNavigate();

  const [center, setCenter] = useState(() => {
    const saved = localStorage.getItem("mapCenter");
    return saved ? JSON.parse(saved) : [18, -22];
  });

  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem("mapZoom");
    return saved ? parseInt(saved) : 6;
  });

  const [selectedNode, setSelectedNode] = useState();
  const [showArmies, setShowArmies] = useState(false);
  const [yesterdayPlayersMap, setYesterdayPlayersMap] = useState({});
  const [armies, setArmies] = useState([]);
  const [filteredArmies, setFilteredArmies] = useState([]);
  const [showArmiesInfo, setShowArmiesInfo] = useState(true);
  const [showSoldiers, setShowSoldiers] = useState(true);
  const [showFriends, setShowFriends] = useState(true);
  const [fontSize, setFontSize] = useState(false);
  const [showScouts, setShowScouts] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showArmiesOnMap, setShowArmiesOnMap] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  const [selectedGridCell, setSelectedGridCell] = useState(null);
  const [history, setHistory] = useState();
  const [selectedDate, setSelectedDate] = useState();
  const [selectedArmies, setSelectedArmies] = useState([]);
  const [startNode, setStartNode] = useState(null);
  const [endNode, setEndNode] = useState(null);
  const [shortestPath, setShortestPath] = useState([]);
  const [isPathfinding, setIsPathfinding] = useState(false);

  const token = window.localStorage.getItem("loggedBLUser");
  let userRole = null;

  if (token) {
    try {
      const decoded = jwtDecode(token);
      userRole = decoded.role;
    } catch (error) {
      // Invalid token
    }
  }

  const historyEntry = selectedDate
    ? history?.filter((h) => h.date === selectedDate).at(-1)
    : null;

  const playersOverride =
    selectedDate && selectedNode
      ? historyEntry?.nodes?.find((n) => Number(n._id) === Number(selectedNode.id))?.players ?? []
      : null;

  useEffect(() => {
    const token = window.localStorage.getItem("loggedBLUser");
    const apiUrl = process.env.REACT_APP_API_URL || "/api";

    axios
      .get(`${apiUrl}/armies?_start=0&_end=1000000`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        setArmies(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (e.response?.status === 401) {
          window.localStorage.removeItem("loggedBLUser");
          window.location.reload();
        }
      });

    axios
      .get(`${apiUrl}/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => setHistory(res.data));

    const getYesterday = () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    };

    const yesterday = getYesterday();

    axios
      .get(`${apiUrl}/history/${yesterday}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const historyArr = res.data;
        const map = {};

        if (Array.isArray(historyArr) && historyArr.length > 0) {
          const history = historyArr[historyArr.length - 1];

          if (history && history.nodes && Array.isArray(history.nodes)) {
            history.nodes.forEach((n) => {
              if (n._id && Array.isArray(n.players)) {
                map[n._id] = n.players;
              }
            });
          }
        }

        setYesterdayPlayersMap(map);
      })
      .catch(() => setYesterdayPlayersMap({}));
  }, []);

  useEffect(() => {
    setFilteredArmies(armies);
  }, [armies]);

  useEffect(() => {
    if (selectedDate) {
      const selectedHistory = history.filter((h) => h.date === selectedDate);
      setArmies(selectedHistory[selectedHistory.length - 1].armies);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (center) {
      localStorage.setItem("mapCenter", JSON.stringify(center));
    }
  }, [center]);

  useEffect(() => {
    localStorage.setItem("mapZoom", zoomLevel);
  }, [zoomLevel]);

  const bounds = latLngBounds(latLng(-40, -180), latLng(90, 30));

  if (window?.chrome) {
    const originalInitTile = GridLayer.prototype._initTile;

    GridLayer.include({
      _initTile: function (tile) {
        originalInitTile.call(this, tile);

        const tileSize = this.getTileSize();

        tile.style.width = `${tileSize.x + 1}px`;
        tile.style.height = `${tileSize.y + 1}px`;
      },
    });
  }

  const selectNode = (node) => {
    setSelectedGridCell(null);
    setSelectedNode(node);
  };

  const closeArmiesBox = () => {
    setShowArmies(false);
  };

  const closeNodeBox = () => {
    setSelectedNode(null);
  };

  const closeHistoryBox = () => {
    window.location.reload();
  };

  const toggleSoldiers = () => {
    setShowSoldiers(!showSoldiers);
  };

  const toggleFriends = () => {
    setShowFriends(!showFriends);
  };

  const toggleArmiesInfo = () => {
    setShowArmiesInfo(!showArmiesInfo);
  };

  const toggleFontSize = () => {
    setFontSize(!fontSize);
  };

  const toggleShowScouts = () => {
    setShowScouts(!showScouts);
  };

  const toggleGrid = () => {
    setShowGrid((prev) => {
      const next = !prev;

      // Turning grid ON
      if (next) {
        setShowArmies(false);
        setShowArmiesOnMap(false);
      }

      // Turning grid OFF
      if (!next) {
        setSelectedGridCell(null);
      }

      return next;
    });
  };

  const handleGridCellClick = ({ x, y }) => {
    setSelectedGridCell({ x, y });
    setSelectedNode(null);
  };

  const toggleArmiesVisibility = () => {
    setShowArmiesOnMap((prev) => {
      const next = !prev;

      if (!next) {
        setShowArmies(false);
        setSelectedArmies([]);
      }

      return next;
    });
  };

  const togglePathfinding = (pathfindingBool) => {
    setIsPathfinding(pathfindingBool);

    if (!pathfindingBool) {
      setStartNode(null);
      setEndNode(null);
      setShortestPath([]);
    }
  };

  const handleNodeClick = (node) => {
    if (!isPathfinding) return;

    if (!startNode) {
      setStartNode(node);
    } else if (!endNode) {
      setEndNode(node);

      const path = findShortestPathBFS(startNode.id, node.id);

      console.log(path);
      setShortestPath(path || []);
    } else {
      setStartNode(null);
      setEndNode(null);
      setShortestPath([]);
    }
  };

  const getPathCoordinates = (path) =>
    path.map((nodeId) => nodes.find((n) => n.id === nodeId)?.position).filter(Boolean);

  const getPolylineWeight = (zoom) => {
    switch (zoom) {
      case 5:
        return 3;
      case 6:
        return 6;
      case 7:
        return 10;
      default:
        return 4;
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <AutocompleteSearchBar
        onSelect={(node) => {
          if (node && node.position) {
            setCenter(null);
            setTimeout(() => {
              setCenter(node.position);
              selectNode(node);
              setZoomLevel(7);
            }, 0);
          }
        }}
        armies={armies}
      />

      {center && (
        <MapContainer
          center={center}
          zoom={zoomLevel}
          scrollWheelZoom={true}
          maxBoundsViscosity={1.0}
          maxBounds={bounds}
          attributionControl={false}
        >
          <MapClickLogger showGrid={showGrid} onGridCellClick={handleGridCellClick} />

          <CachedTileLayer
            url="../map/{z}/{x}/{y}.webp"
            minZoom={5}
            maxNativeZoom={6}
            maxZoom={9}
            noWrap={true}
          />

          {showGrid && (
            <GridOverlay zoomLevel={zoomLevel} selectedGridCell={selectedGridCell} />
          )}

          {nodes.map((node) => (
            <NodeMarker
              key={`node-${node.id}`}
              position={node.position}
              zoomLevel={zoomLevel}
              armies={showArmiesOnMap ? filteredArmies : []}
              node={node}
              isSelected={selectedNode?.id === node.id}
              showArmiesInfo={showArmiesInfo}
              showSoldiers={showSoldiers}
              showFriends={showFriends}
              fontSize={fontSize}
              showScouts={showScouts}
              selectedArmies={selectedArmies}
              setSelectedArmies={setSelectedArmies}
              eventHandlers={{
                click: () => {
                  selectNode(node);
                  setShowArmies(false);

                  if (isPathfinding) {
                    handleNodeClick(node);
                  }
                },
              }}
            />
          ))}

          {showArmiesOnMap &&
            nodes.map((node) => {
              const visibleArmiesOnNode = filteredArmies.filter((army) => army.node === node.id);

              if (visibleArmiesOnNode.length === 0) {
                return null;
              }

              const armiesOnNode = armies.filter((army) => army.node === node.id);

              return (
                <ArmyMarker
                  key={`army-node-${node.id}`}
                  position={node.position}
                  zoomLevel={zoomLevel}
                  armies={armiesOnNode}
                  nodes={nodes}
                  selectedArmies={selectedArmies}
                  showTooltip
                  eventHandlers={{
                    click: () => {
                      selectNode(node);
                      setShowArmies(false);
                    },
                  }}
                />
              );
            })}

          <Control position="topleft">
            <div className={classes.controlPanel}>
              <ul className={classes.mapMenu}>
                <ControlButton title="Armies">
                  <icons.GiKnightBanner
                    size={32}
                    onClick={() => {
                      setSelectedNode(null);
                      setShowArmies(true);
                    }}
                  />
                </ControlButton>

                <ControlButton title={showArmiesOnMap ? "Hide Armies" : "Show Armies"}>
                  {showArmiesOnMap ? (
                    <icons.RiFlagOffFill onClick={toggleArmiesVisibility} />
                  ) : (
                    <icons.RiFlagFill onClick={toggleArmiesVisibility} />
                  )}
                </ControlButton>

                <ControlButton title={showGrid ? "Hide Ships" : "Show Ships"}>
                  {showGrid ? (
                    <icons.TbShipOff onClick={toggleGrid} />
                  ) : (
                    <icons.TbShip onClick={toggleGrid} />
                  )}
                </ControlButton>

                <ControlButton title="History">
                  <icons.MdHistory
                    onClick={() => {
                      setShowHistory(true);
                    }}
                    color={showHistory ? "#d9534f" : undefined}
                  />
                </ControlButton>

                <ControlButton title={showArmiesInfo ? "Hide Army Info" : "Show Army Info"}>
                  {showArmiesInfo ? (
                    <icons.AiFillEyeInvisible onClick={toggleArmiesInfo} />
                  ) : (
                    <icons.AiFillEye onClick={toggleArmiesInfo} />
                  )}
                </ControlButton>

                <ControlButton title={showSoldiers ? "Hide Soldiers Count" : "Show Soldiers Count"}>
                  {showSoldiers ? (
                    <icons.MdOutlinePersonOff onClick={toggleSoldiers} />
                  ) : (
                    <icons.MdOutlinePersonOutline onClick={toggleSoldiers} />
                  )}
                </ControlButton>

                <ControlButton title="Toggle Friendly Soldiers Count">
                  <icons.GiHeartTower onClick={toggleFriends} />
                </ControlButton>

                <ControlButton title="Toggle Font Size">
                  <icons.TiSortAlphabeticallyOutline onClick={toggleFontSize} />
                </ControlButton>

                <ControlButton title={showScouts ? "Hide Scouts" : "Show Scouts"}>
                  <icons.BsIncognito
                    onClick={toggleShowScouts}
                    color={showScouts ? undefined : "#d9534f"}
                  />
                </ControlButton>

                {/*
                <ControlButton title={isPathfinding ? "Disable Travel Mode" : "Enable Travel Mode"}>
                  <icons.GiLeatherBoot
                    onClick={() => togglePathfinding(!isPathfinding)}
                    color={isPathfinding ? "#d9534f" : undefined}
                  />
                </ControlButton>
                */}
              </ul>
            </div>
          </Control>

          <Control position="topright">
            <div className={classes.control}>
              {selectedNode && (
                <NodeBox
                  node={selectedNode}
                  armies={filteredArmies}
                  onClose={closeNodeBox}
                  disabled={!!selectedDate}
                  playersOverride={playersOverride}
                  yesterdayPlayersMap={yesterdayPlayersMap}
                />
              )}

              {showArmies && (
                <ArmiesBox
                  armies={armies}
                  filteredArmies={filteredArmies}
                  setFilteredArmies={setFilteredArmies}
                  nodes={nodes}
                  onClose={closeArmiesBox}
                  setCenter={setCenter}
                  disabled={selectedDate ? true : false}
                />
              )}
            </div>

            {showGrid && selectedGridCell && (
              <GridCellBox
                gridCell={selectedGridCell}
                userRole={userRole}
                onClose={() => setSelectedGridCell(null)}
              />
            )}
          </Control>

          <Control position="bottomleft">
            {showHistory && (
              <HistoryBox
                data={history}
                onClose={closeHistoryBox}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
            )}

            <ul>
              {userRole === "admin" && (
                <ControlButton title="Admin Panel">
                  <icons.MdAdminPanelSettings onClick={() => navigate("/admin")} />
                </ControlButton>
              )}

              <ControlButton title="Log Out">
                <icons.RiLogoutBoxFill onClick={auth.logout} />
              </ControlButton>
            </ul>
          </Control>

          <ZoomListener setZoomLevel={setZoomLevel} />
          <MoveListener setCenter={setCenter} />
          <AttributionControl prefix="Renaissance Kingdoms Maps" />

          {isPathfinding && (
            <Polyline
              key={zoomLevel}
              positions={getPathCoordinates(shortestPath)}
              color="red"
              weight={getPolylineWeight(zoomLevel)}
            />
          )}
        </MapContainer>
      )}
    </div>
  );
};

export default RKMap;